import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Logo from "./Logo";

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const menuRef = useRef(null);

  /* ---------------- Dark Mode (persistiert) ---------------- */
  useEffect(() => {
    const theme = localStorage.getItem("theme");
    if (theme === "dark") {
      document.documentElement.classList.add("dark");
    }
  }, []);

  const toggleDarkMode = () => {
    const isDark = document.documentElement.classList.toggle("dark");
    localStorage.setItem("theme", isDark ? "dark" : "light");
  };

  /* ---------------- Page Name ---------------- */
  const getPageName = () => {
    const names = {
      "/": "Home",
      "/fracht": "Fracht",
      "/leerfahrten": "Leer-Fahrten",
      "/pricing": "Preise",
      "/jobs": "Jobs",
      "/contact": "Kontakt",
    };
    return names[location.pathname] || "";
  };

  /* ---------------- Active Link ---------------- */
  const linkClass = (path) =>
    `relative py-1 transition ${
      location.pathname === path
        ? "text-blue-500 font-semibold after:absolute after:left-0 after:-bottom-1 after:h-[2px] after:w-full after:bg-blue-500"
        : "hover:text-blue-500"
    }`;

  /* ---------------- Click outside + ESC ---------------- */
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (isOpen && menuRef.current && !menuRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };

    const handleEsc = (e) => {
      if (e.key === "Escape") setIsOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEsc);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEsc);
    };
  }, [isOpen]);

  return (
    <nav className="fixed top-0 left-0 w-full bg-white dark:bg-zinc-900 text-zinc-900 dark:text-zinc-100 shadow-md z-50">
      <div className="max-w-[1200px] mx-auto px-6 h-16 flex items-center justify-between">

        {/* LOGO */}
        <Link to="/" className="flex items-center gap-3">
          <Logo className="h-9 w-auto" />
          <span className="hidden sm:inline text-lg font-semibold tracking-tight">
            | <span className="font-light opacity-80">{getPageName()}</span>
          </span>
        </Link>

        {/* DESKTOP MENU */}
        <ul className="hidden md:flex items-center gap-6">
          <li><Link to="/" className={linkClass("/")}>Home</Link></li>
          <li><Link to="/fracht" className={linkClass("/fracht")}>Fracht</Link></li>
          <li><Link to="/leerfahrten" className={linkClass("/leerfahrten")}>Leer-Fahrten</Link></li>
          <li><Link to="/pricing" className={linkClass("/pricing")}>Preise</Link></li>
          <li><Link to="/jobs" className={linkClass("/jobs")}>Jobs</Link></li>
          <li><Link to="/contact" className={linkClass("/contact")}>Kontakt</Link></li>

          <li>
            <button
              onClick={toggleDarkMode}
              className="ml-4 px-2 py-1 rounded hover:bg-gray-200 dark:hover:bg-zinc-700 transition"
              aria-label="Toggle dark mode"
            >
              🌙
            </button>
          </li>
        </ul>

        {/* MOBILE TOGGLE */}
        <button
          className="md:hidden text-2xl"
          onClick={() => setIsOpen(true)}
          aria-label="Open menu"
        >
          ☰
        </button>
      </div>

      {/* ---------------- MOBILE SLIDE-IN MENU ---------------- */}
      <div
        ref={menuRef}
        className={`fixed top-0 right-0 h-full w-64 bg-white dark:bg-zinc-900 shadow-lg transform transition-transform duration-300
        ${isOpen ? "translate-x-0" : "translate-x-full"}`}
      >
        <div className="flex justify-between items-center px-6 h-16 border-b dark:border-zinc-700">
          <span className="font-semibold">Menü</span>
          <button
            onClick={() => setIsOpen(false)}
            className="text-2xl"
            aria-label="Close menu"
          >
            ✕
          </button>
        </div>

        <div className="flex flex-col px-6 py-4 space-y-4">
          <Link onClick={() => setIsOpen(false)} to="/" className={linkClass("/")}>Home</Link>
          <Link onClick={() => setIsOpen(false)} to="/fracht" className={linkClass("/fracht")}>Fracht</Link>
          <Link onClick={() => setIsOpen(false)} to="/leerfahrten" className={linkClass("/leerfahrten")}>Leer-Fahrten</Link>
          <Link onClick={() => setIsOpen(false)} to="/pricing" className={linkClass("/pricing")}>Preise</Link>
          <Link onClick={() => setIsOpen(false)} to="/jobs" className={linkClass("/jobs")}>Jobs</Link>
          <Link onClick={() => setIsOpen(false)} to="/contact" className={linkClass("/contact")}>Kontakt</Link>

          <button
            onClick={toggleDarkMode}
            className="mt-6 px-3 py-2 rounded bg-gray-100 dark:bg-zinc-800"
          >
            🌙 Dark Mode
          </button>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
